import React, { useState, useEffect } from 'react'

import { Dialog, Button } from '@clientbase/clientbase-library'
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material'

import { Tag } from 'components/TagsAddInput/TagsAddInput'

import { theme } from 'styles/theme'

export type MetricTagOption = 'MRR' | 'AVULSA' | 'NONE'

interface MetricTagDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (selectedOption: MetricTagOption, tag: Tag | null) => void
  totalCycles: number
  availableTags: Tag[]
  loading?: boolean
}

/**
 * Determines the default metric tag option based on total_cycles
 * - total_cycles = 0 or >= 6: MRR (recurring revenue)
 * - total_cycles 1-5: AVULSA (one-time revenue)
 */
export const getDefaultMetricOption = (
  totalCycles: number
): MetricTagOption => {
  if (totalCycles === 0 || totalCycles >= 6) {
    return 'MRR'
  }
  if (totalCycles >= 1 && totalCycles <= 5) {
    return 'AVULSA'
  }
  return 'MRR' // fallback
}

/**
 * Finds MRR or AVULSA tag from available tags (case-insensitive)
 */
export const findMetricTag = (
  availableTags: Tag[],
  tagName: 'MRR' | 'AVULSA'
): Tag | undefined => {
  return availableTags.find(
    (tag) => tag.name.toUpperCase() === tagName.toUpperCase()
  )
}

/**
 * Checks if the tags array already contains MRR or AVULSA tag
 */
export const hasMetricTag = (tags: Tag[]): boolean => {
  if (!tags || tags.length === 0) return false
  return tags.some(
    (tag) =>
      tag.name.toUpperCase() === 'MRR' || tag.name.toUpperCase() === 'AVULSA'
  )
}

const MetricTagDialog: React.FC<MetricTagDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  totalCycles,
  availableTags,
  loading = false
}) => {
  const [selectedOption, setSelectedOption] = useState<MetricTagOption>('MRR')

  // Set default option based on total_cycles when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedOption(getDefaultMetricOption(totalCycles))
    }
  }, [isOpen, totalCycles])

  const handleConfirm = () => {
    let tag: Tag | null = null

    if (selectedOption === 'MRR') {
      tag = findMetricTag(availableTags, 'MRR') || null
    } else if (selectedOption === 'AVULSA') {
      tag = findMetricTag(availableTags, 'AVULSA') || null
    }

    onConfirm(selectedOption, tag)
  }

  const mrrTag = findMetricTag(availableTags, 'MRR')
  const avulsaTag = findMetricTag(availableTags, 'AVULSA')

  return (
    <Dialog
      icon="barChart"
      title="Classificação para métricas"
      description="Como você gostaria que este plano fosse contabilizado nas métricas de receita?"
      isOpenDialog={isOpen}
      setIsOpenDialog={onClose}
    >
      <Box sx={{ width: '100%' }}>
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <RadioGroup
            value={selectedOption}
            onChange={(e) =>
              setSelectedOption(e.target.value as MetricTagOption)
            }
          >
            <FormControlLabel
              value="MRR"
              control={<Radio sx={{ mt: 0.5 }} />}
              label={
                <Box sx={{ ml: 1 }}>
                  <Typography
                    variant="smBold"
                    component="div"
                    color={theme.palette.neutral[200]}
                  >
                    MRR (Receita Recorrente Mensal)
                  </Typography>
                  <Typography
                    variant="xsLight"
                    component="div"
                    color={theme.palette.neutral[400]}
                  >
                    Contabilizar como receita recorrente nas métricas
                  </Typography>
                  {!mrrTag && (
                    <Typography
                      variant="xsLight"
                      component="div"
                      color={theme.palette.warning.main}
                      sx={{ mt: 0.5 }}
                    >
                      A tag MRR será criada automaticamente
                    </Typography>
                  )}
                </Box>
              }
              sx={{
                alignItems: 'flex-start',
                py: 1.5,
                borderBottom: `1px solid ${theme.palette.neutral[700]}`,
                mx: 0,
                width: '100%'
              }}
            />
            <FormControlLabel
              value="AVULSA"
              control={<Radio sx={{ mt: 0.5 }} />}
              label={
                <Box sx={{ ml: 1 }}>
                  <Typography
                    variant="smBold"
                    component="div"
                    color={theme.palette.neutral[200]}
                  >
                    Receita Avulsa
                  </Typography>
                  <Typography
                    variant="xsLight"
                    component="div"
                    color={theme.palette.neutral[400]}
                  >
                    Contabilizar como receita avulsa (não recorrente) nas
                    métricas
                  </Typography>
                  {!avulsaTag && (
                    <Typography
                      variant="xsLight"
                      component="div"
                      color={theme.palette.warning.main}
                      sx={{ mt: 0.5 }}
                    >
                      A tag AVULSA será criada automaticamente
                    </Typography>
                  )}
                </Box>
              }
              sx={{
                alignItems: 'flex-start',
                py: 1.5,
                borderBottom: `1px solid ${theme.palette.neutral[700]}`,
                mx: 0,
                width: '100%'
              }}
            />
            <FormControlLabel
              value="NONE"
              control={<Radio sx={{ mt: 0.5 }} />}
              label={
                <Box sx={{ ml: 1 }}>
                  <Typography
                    variant="smBold"
                    component="div"
                    color={theme.palette.neutral[200]}
                  >
                    Não categorizar
                  </Typography>
                  <Typography
                    variant="xsLight"
                    component="div"
                    color={theme.palette.neutral[400]}
                  >
                    Aparecerá em receita, mas não será classificado como MRR ou
                    Avulsa
                  </Typography>
                </Box>
              }
              sx={{
                alignItems: 'flex-start',
                py: 1.5,
                mx: 0,
                width: '100%'
              }}
            />
          </RadioGroup>
        </FormControl>

        <Button
          fullWidth
          onClick={handleConfirm}
          disabled={loading}
          loading={loading}
          sx={{ mt: 3 }}
        >
          Confirmar e salvar
        </Button>
      </Box>
    </Dialog>
  )
}

export default MetricTagDialog
